import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {Alert, Backdrop, Box, Button, CircularProgress, Tooltip} from '@mui/material';
import dayjs from 'dayjs';
import {t} from 'i18next';
import update from 'immutability-helper';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {useAppSelector} from '../../../hooks/redux';
import {DashboardPanelData} from '../../../interfaces/Dashboard';
import {
  HazardEventLog,
  HazardEventLogListQuery,
  HazardEventLogListResponse,
} from '../../../interfaces/HazardAILog';
import reduxSelectors from '../../../redux/selectors';
import {saveFile} from '../../../utils/file';
import SpeedSliderSelector from '../../common/SpeedSliderSelector';
import {
  usePanel,
  usePanelFilter,
} from '../../dashboards/entities/DashboardEntityContext';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import {SafeyeNodeSelector} from '../../selectors/SafeyeNodeSelector';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';
import {HeatmapObjectSelector} from '../HazardHeatmap/HeatmapObjectSelector';
import {HeatmapZoneSelector} from '../HazardHeatmap/HeatmapZoneSelector';
import {CameraDetectionsModal} from './CameraDetectionsModal';
import {CameraGrid} from './CameraGrid';
import {PositionTable} from './PositionTable';

interface Props {
  value?: DashboardPanelData;
  onUpdate?: (value?: DashboardPanelData) => void;
}

export const MachineDetectionsByCamera = (props?: Props) => {
  const isDarkMode = useSelector(reduxSelectors.app.getIsDarkMode);
  const safeyeNodes = useAppSelector(({assets}) => assets.safeye_nodes);
  const assetMachines = useAppSelector(({assets}) => assets.asset_machines);
  const machineTypes = useAppSelector(({assets}) => assets.machine_types);
  const [panel] = usePanel();
  const {filter, setFilter} = usePanelFilter();

  const [fetchedItems, setFetchedItems] = useState<HazardEventLog[]>([]);
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedCamId, setSelectedCamId] = useState<number | null>(null);

  const params = useMemo<HazardEventLogListQuery & {machine_id?: number}>(
    () => ({
      machine_id:
        filter.params?.machine_id ?? props?.value?.params?.machine_id ?? undefined,
      date_start:
        filter.params?.date_start ??
        props?.value?.params?.date_start ??
        dayjs().format('YYYY-MM-DD'),
      date_end:
        filter.params?.date_end ??
        props?.value?.params?.date_end ??
        dayjs().format('YYYY-MM-DD'),
      object: filter.params?.object ?? undefined,
      zone: filter.params?.zone ?? undefined,
      speed_min: filter.params?.speed_min ?? 0,
      speed_max: filter.params?.speed_max ?? 200,
      page: 0,
      limit: 10000,
      order: 'id',
      dir: 'DESC',
    }),
    [filter]
  );

  useEffect(() => {
    if (params?.machine_id) {
      fetchData(params);
      props?.onUpdate?.({...props.value, params});
    } else {
      setFetchedItems([]);
    }
  }, [params]);

  const fetchData = useCallback(
    async (p: typeof params) => {
      if (!p.machine_id) {
        return;
      }
      setFetchedInProgress(true);
      setFetchedErrors([]);
      try {
        const {machine_id, ...queryParams} = p;
        const resp = await API.get<HazardEventLogListResponse>(
          `${apiBaseUrl}/hazard-ai/${machine_id}/detection-event-log`,
          {params: queryParams}
        );
        setFetchedItems(resp.data.items);
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, 'STRA_mdb_cam01');
        setFetchedErrors(messages);
      }
      setFetchedInProgress(false);
    },
    [params]
  );

  const handleExport = async () => {
    if (!params.machine_id) {
      return;
    }
    setIsExporting(true);
    try {
      const {machine_id, ...queryParams} = params;
      const resp = await API.get(
        `${apiBaseUrl}/hazard-ai/${machine_id}/detection-by-camera`,
        {params: {...queryParams, export: 'excel'}, responseType: 'blob'}
      );
      saveFile(
        resp.data,
        `machine_detections_by_camera_${params.date_start}_${params.date_end}.xlsx`
      );
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_mdb_cam_xls');
      setFetchedErrors(messages);
    }
    setIsExporting(false);
  };

  // Aggregate detections by cam_id
  const cameraCounts = useMemo<Record<number, number>>(() => {
    const acc: Record<number, number> = {};
    fetchedItems.forEach((item) => {
      if (item.cam_id != null) {
        acc[item.cam_id] = (acc[item.cam_id] ?? 0) + 1;
      }
    });
    return acc;
  }, [fetchedItems]);

  const total = useMemo(
    () => Object.values(cameraCounts).reduce((sum, n) => sum + n, 0),
    [cameraCounts]
  );

  const selectedMachineInfo = useMemo(() => {
    if (!params.machine_id) {
      return {name: null, typeName: null};
    }
    const node = safeyeNodes?.find((n) => n.id === params.machine_id);
    if (!node) {
      return {name: null, typeName: null};
    }
    const machine = assetMachines?.find(
      (m) => `${m.safeye_node_id ?? ''}` === `${node.safeye_node_id}`
    );
    if (!machine) {
      return {name: null, typeName: null};
    }
    const machineType = machineTypes?.find(
      (t) => t.id === machine.type_id
    );
    return {name: machine.name, typeName: machineType?.name ?? null};
  }, [params.machine_id, safeyeNodes, assetMachines, machineTypes]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="calc(100% - 38px)"
      width="100%"
      overflow="hidden"
      bgcolor={isDarkMode ? 'background.default' : 'grey.100'}
    >
      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <DashboardPanelTitleSlot>
        {t(`panels.${panel?.code}`)}
      </DashboardPanelTitleSlot>

      {/* Filters */}
      <Box
        display="flex"
        gap={1}
        py={1}
        flexWrap="wrap"
        bgcolor={isDarkMode ? 'grey.900' : '#FFF'}
      >
        <Box maxWidth={300} minWidth={180}>
          <SafeyeNodeSelector
            size="small"
            fullWidth
            nullLabel="Select a Machine"
            value={params.machine_id ?? null}
            onChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    machine_id: {$set: v as number},
                  },
                })
              );
            }}
          />
        </Box>
        <Box maxWidth={400}>
          <DateRangeSelect
            value={[
              dayjs(params.date_start).toDate(),
              dayjs(params.date_end).toDate(),
            ]}
            size="small"
            onChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    date_start: {
                      $set: v?.[0]
                        ? dayjs(v?.[0]).format('YYYY-MM-DD')
                        : undefined,
                    },
                    date_end: {
                      $set: v?.[1]
                        ? dayjs(v?.[1]).format('YYYY-MM-DD')
                        : undefined,
                    },
                  },
                })
              );
            }}
          />
        </Box>
        <Box maxWidth={300} minWidth={160}>
          <HeatmapObjectSelector
            size="small"
            fullWidth
            value={params.object}
            onChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    object: {$set: v ?? undefined},
                  },
                })
              );
            }}
          />
        </Box>
        <Box maxWidth={300} minWidth={160}>
          <HeatmapZoneSelector
            size="small"
            fullWidth
            value={params.zone}
            onChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    zone: {$set: v},
                  },
                })
              );
            }}
          />
        </Box>
        <Box>
          <SpeedSliderSelector
            min={0}
            max={200}
            lowerValue={Number(params.speed_min)}
            upperValue={Number(params.speed_max)}
            onLowerChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    speed_min: {$set: v as number},
                  },
                })
              );
            }}
            onUpperChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    speed_max: {$set: v as number},
                  },
                })
              );
            }}
          />
        </Box>
        <Box ml="auto" display="flex" alignItems="center">
          <Tooltip title="Export to Excel">
            <Button
              size="small"
              variant="outlined"
              disabled={
                fetchedInProgress ||
                isExporting ||
                !params.machine_id ||
                total === 0
              }
              onClick={handleExport}
            >
              <FileDownloadIcon />
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Errors */}
      {fetchedErrors.map((error, idx) => (
        <Alert
          key={idx}
          severity="error"
          onClose={() => params?.machine_id && fetchData(params)}
        >
          {error}
        </Alert>
      ))}

      {/* Main content: two columns */}
      <Box
        display="flex"
        flex={1}
        overflow="hidden"
        gap={1}
        minHeight={0}
      >
        {/* Left column: 3x3 camera grid */}
        <Box
          flex={1}
          overflow="hidden"
          bgcolor={isDarkMode ? 'background.paper' : 'background.paper'}
          borderRadius={1}
          border="1px solid"
          borderColor="divider"
        >
          <CameraGrid
            counts={cameraCounts}
            machineName={selectedMachineInfo.name}
            machineType={selectedMachineInfo.typeName}
            onCameraClick={setSelectedCamId}
          />
        </Box>

        {/* Right column: pie chart + table */}
        <Box
          flex={1}
          overflow="hidden"
          display="flex"
          flexDirection="column"
          bgcolor={isDarkMode ? 'background.paper' : 'background.paper'}
          borderRadius={1}
          border="1px solid"
          borderColor="divider"
        >
          <PositionTable counts={cameraCounts} total={total} />
        </Box>
      </Box>

      {selectedCamId != null && (
        <CameraDetectionsModal
          open
          camId={selectedCamId}
          items={fetchedItems.filter((i) => i.cam_id === selectedCamId)}
          onClose={() => setSelectedCamId(null)}
        />
      )}
    </Box>
  );
};
